import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api, SITE_URL } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import LeadForm from "@/components/LeadForm";
import CtaBlock from "@/components/CtaBlock";
import { Briefcase, MapPin, Clock, Rocket, Heart, TrendingUp } from "lucide-react";

export default function Careers() {
  const { company } = useCompany();
  const { data: jobs = [] } = useQuery({ queryKey: ["jobs"], queryFn: async () => (await api.get("/jobs")).data });

  const jsonLd = jobs.map((j) => ({
    "@context": "https://schema.org", "@type": "JobPosting",
    title: j.title, description: j.description,
    employmentType: j.employment_type, hiringOrganization: { "@type": "Organization", name: company.name },
    jobLocation: { "@type": "Place", address: { "@type": "PostalAddress", addressLocality: j.location || company.city, addressRegion: "FL", addressCountry: "US" } },
  }));

  return (
    <>
      <Seo title="Careers & HVAC Jobs | Aquila Air & Heating" description="Join the Aquila Air & Heating team on Florida's Space Coast. Explore HVAC careers with a growing, locally owned company that values its technicians." path="/careers/" jsonLd={jsonLd} />
      <section className="bg-navy text-white py-12 md:py-16 dot-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="[&_*]:!text-slate-300"><Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Careers" }]} /></div>
          <p className="mt-5 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-launch"><Rocket className="h-4 w-4" /> Build Your Career With Us</p>
          <h1 className="mt-3 text-4xl sm:text-5xl font-extrabold tracking-tight">Careers at Aquila Air &amp; Heating</h1>
          <p className="mt-4 max-w-2xl text-lg text-slate-200">We're building the Space Coast's best HVAC team. If you take pride in your work and treat customers right, we want to hear from you.</p>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-3 gap-6 mb-12">
            {[{ i: Heart, t: "Great Culture", d: "A supportive, family-feel team that has your back." },
              { i: TrendingUp, t: "Room to Grow", d: "Training and advancement for motivated technicians." },
              { i: Briefcase, t: "Competitive Pay", d: "Strong compensation and benefits for the right people." }].map((b, i) => {
              const Icon = b.i;
              return <div key={i} className="bg-white border border-slate-200 rounded-lg p-6"><Icon className="h-8 w-8 text-cool" /><h3 className="mt-3 font-semibold text-navy">{b.t}</h3><p className="mt-1 text-sm text-slate-600">{b.d}</p></div>;
            })}
          </div>

          <div className="grid lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2">
              <h2 className="text-2xl font-bold text-navy mb-5">Open Positions</h2>
              {jobs.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-lg p-8 text-center">
                  <p className="text-slate-600">We don't have specific openings posted right now, but we're always looking for great HVAC talent. Send us your info and we'll reach out when a spot opens up.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {jobs.map((j) => (
                    <div key={j.id} data-testid={`job-${j.id}`} className="bg-white border border-slate-200 rounded-lg p-6">
                      <h3 className="text-xl font-semibold text-navy">{j.title}</h3>
                      <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-500">
                        <span className="flex items-center gap-1"><MapPin className="h-4 w-4" /> {j.location || "Space Coast, FL"}</span>
                        <span className="flex items-center gap-1"><Clock className="h-4 w-4" /> {j.employment_type || "Full-time"}</span>
                      </div>
                      <p className="mt-3 text-sm text-slate-600 leading-relaxed">{j.description}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <aside>
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 lg:sticky lg:top-24">
                <h3 className="text-xl font-bold text-navy">Apply Now</h3>
                <p className="text-sm text-slate-600 mt-1 mb-4">Tell us a bit about yourself and we'll be in touch.</p>
                <LeadForm defaultService="Careers / Employment" compact />
              </div>
            </aside>
          </div>
        </div>
      </section>
      <CtaBlock heading="Ready to Launch Your Career?" sub="Reach out today and take the next step with a growing Space Coast HVAC company." />
    </>
  );
}
